/**
 * Default theme colors for DataTable.
 * These can be overridden via the `theme` prop.
 */
export const defaultDataTableTheme = {
  headerBg: "var(--mela-surface-card)",
  headerTextColor: "var(--mela-text-secondary)",
  footerBg: "var(--mela-surface-card)",
  rowBg: "var(--mela-surface-card)",
  rowHoverBg: "var(--mela-surface-subtle)",
  rowSelectedBg: "var(--mela-color-primary-soft-bg)",
  rowSelectedHoverBg: "var(--mela-color-primary-soft-hover-bg)",
  filterBg: "var(--mela-surface-card)",
  borderColor: "var(--mela-border-subtle)",
  cellTextColor: "var(--mela-text-secondary)",
} as const;

export type DataTableDefaultTheme = typeof defaultDataTableTheme;

/**
 * Density styles for DataTable.
 * Centralized here to ensure consistency across all components.
 */
export const DENSITY_STYLES = {
  compact: { padding: "8px 8px", fontSize: "0.8125rem" },
  standard: { padding: "12px 12px", fontSize: "0.875rem" },
  comfortable: { padding: "16px 16px", fontSize: "0.9375rem" },
} as const;

export type DataTableDensity = keyof typeof DENSITY_STYLES;

export const getDensityPaddingWithLeft = (
  densityPadding: string,
  leftPadding: number,
) => {
  const [verticalPadding = "0", horizontalPadding = verticalPadding] =
    densityPadding.split(" ");

  return `${verticalPadding} ${horizontalPadding} ${verticalPadding} ${leftPadding}px`;
};

/**
 * Column width constants for special columns.
 */
export const COLUMN_WIDTHS = {
  /** Selection checkbox and expand button column width */
  checkbox: 35,

  /** Aesthetic padding for the first column */
  firstColumnPadding: 24,

  /** Fallback width when column width is not specified */
  fallback: 100,
  /** Minimum width when resizing columns */
  minResize: 50,
  /** Maximum width when resizing columns */
  maxResize: 800,
} as const;

/**
 * Fixed layout for the structural row-controls column.
 */
export const ROW_CONTROLS_LAYOUT = {
  slotWidth: 28,
  gap: 2,
  paddingInline: 0,
} as const;

export const getRowControlsWidth = (slots: number): number => {
  const normalizedSlots = Math.max(1, Math.floor(slots));
  return (
    normalizedSlots * ROW_CONTROLS_LAYOUT.slotWidth +
    (normalizedSlots - 1) * ROW_CONTROLS_LAYOUT.gap +
    ROW_CONTROLS_LAYOUT.paddingInline * 2
  );
};

/**
 * Shadow styles for pinned columns when scrolling.
 */
export const PINNED_SHADOWS = {
  /** Shadow for the last pinned-left column (shadow on right side) */
  left: "4px 0 8px -4px rgba(15, 23, 42, 0.12)",
  /** Shadow for the first pinned-right column (shadow on left side) */
  right: "-4px 0 8px -4px rgba(15, 23, 42, 0.12)",
} as const;

/**
 * Z-index layering for table elements.
 * Higher values appear above lower values.
 */
export const TABLE_Z_INDEX = {
  /** Sticky thead/tfoot */
  stickyHeaderFooter: 10,
  /** Header cells (above body cells) */
  headerCell: 3,
  /** Pinned columns in header/filter row */
  pinnedHeader: 2,
  /** Pinned cells in body rows */
  pinnedBody: 1,
} as const;

/**
 * Border thickness constants.
 */
export const TABLE_BORDERS = {
  /** Normal border (1px) */
  normal: "1px",
  /** Emphasized border for header/footer (2px) */
  emphasis: "2px",
} as const;

/**
 * Transition styles for animations.
 */
export const TABLE_TRANSITIONS = {
  /** Shadow transition on scroll */
  shadow: "box-shadow 0.3s ease",
} as const;

/**
 * Filter input heights based on density.
 */
export const FILTER_INPUT_HEIGHTS = {
  compact: 28,
  standard: 32,
  comfortable: 36,
} as const;
